import { useEffect, useMemo, useState } from 'react';
import { Check, Copy, KeyRound, UserCog } from 'lucide-react';
import Button from '../../../components/commoncomponents/Button';
import Card from '../../../components/commoncomponents/Card';
import ConfirmationModal from '../../../components/commoncomponents/ConfirmationModal';
import EmptyState from '../../../components/commoncomponents/EmptyState';
import PaginationControls from '../../../components/commoncomponents/PaginationControls';
import SearchFilterBar from '../../../components/commoncomponents/SearchFilterBar';
import Table from '../../../components/commoncomponents/Table';
import TableActionButtons from '../../../components/commoncomponents/TableActionButtons';
import { getBranches } from '../../../services/branchService';
import {
  createUser,
  getUsers,
  resetUserPassword,
  updateUser,
} from '../../../services/userService';
import UserFormModal from './UserFormModal';
import {
  ADMIN_USER_TYPE,
  DEFAULT_ACCOUNTANT_SEAWEED_ACCESS_TABS,
  DEFAULT_ROLE_NAME,
  SEAWEED_ADMIN_TAB_OPTIONS,
  STAFF_USER_TYPE,
  getRoleOption,
  getUserTypeForRole,
} from '../constants/userRoles';

const DEFAULT_PAGE_SIZE = 10;

function UserManagementView({ currentUser, token, businesses, selectedBusinessId }) {
  const selectedBusiness = useMemo(
    () => businesses.find((item) => item.id === selectedBusinessId) || null,
    [businesses, selectedBusinessId]
  );
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editingUserId, setEditingUserId] = useState(null);
  const [isEditingFirstAdminAccount, setIsEditingFirstAdminAccount] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState(null);
  const [resetPasswordTarget, setResetPasswordTarget] = useState(null);
  const [resetPasswordResult, setResetPasswordResult] = useState(null);
  const [resetPasswordError, setResetPasswordError] = useState('');
  const [resetPasswordSubmitting, setResetPasswordSubmitting] = useState(false);
  const [hasCopiedTemporaryPassword, setHasCopiedTemporaryPassword] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
  });
  const [page, setPage] = useState(1);
  const [formValues, setFormValues] = useState({
    username: '',
    email: '',
    password: '',
    role_name: DEFAULT_ROLE_NAME,
    branch_id: '',
    status: 'active',
    has_access: true,
    seaweed_access_tabs: DEFAULT_ACCOUNTANT_SEAWEED_ACCESS_TABS,
  });

  useEffect(() => {
    let ignore = false;

    async function loadData() {
      if (!selectedBusinessId) {
        setUsers([]);
        setBranches([]);
        setLoading(false);
        setError('');
        return;
      }

      setLoading(true);
      setError('');

      try {
        const [usersResponse, branchesResponse] = await Promise.all([
          getUsers(token, { customer_id: selectedBusinessId }),
          getBranches(token, selectedBusinessId),
        ]);

        if (!ignore) {
          setUsers(usersResponse.data || []);
          setBranches(branchesResponse.data || []);
        }
      } catch (requestError) {
        if (!ignore) {
          setError(requestError.message);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    loadData();

    return () => {
      ignore = true;
    };
  }, [selectedBusinessId, token]);

  useEffect(() => {
    setPage(1);
  }, [filters, selectedBusinessId]);

  function openModal() {
    setModalMode('create');
    setEditingUserId(null);
    setIsEditingFirstAdminAccount(false);
    setSubmitError('');
    setFormValues({
      username: '',
      email: '',
      password: '',
      role_name: DEFAULT_ROLE_NAME,
      branch_id: branches[0]?.id ? String(branches[0].id) : '',
      status: 'active',
      has_access: true,
      seaweed_access_tabs: DEFAULT_ACCOUNTANT_SEAWEED_ACCESS_TABS,
    });
    setIsModalOpen(true);
  }

  function openEditModal(row) {
    setModalMode('edit');
    setEditingUserId(row.id);
    setIsEditingFirstAdminAccount(Boolean(row.is_first_admin_account));
    setSubmitError('');
    setFormValues({
      username: row.username,
      email: row.email,
      password: '',
      role_name: row.role_name,
      branch_id: row.branch_id ? String(row.branch_id) : '',
      status: row.status,
      has_access: row.status === 'active',
      seaweed_access_tabs: Array.isArray(row.seaweed_access_tabs)
        ? row.seaweed_access_tabs
        : DEFAULT_ACCOUNTANT_SEAWEED_ACCESS_TABS,
    });
    setIsModalOpen(true);
  }

  function closeModal(forceClose = false) {
    if (submitting && !forceClose) {
      return;
    }

    setIsModalOpen(false);
    setEditingUserId(null);
    setIsEditingFirstAdminAccount(false);
    setModalMode('create');
    setSubmitError('');
    setSubmitting(false);
  }

  function handleChange(event) {
    const { name, value, type, checked, dataset } = event.target;
    const fieldName = dataset.field || name;
    setFormValues((current) => ({
      ...current,
      [fieldName]: type === 'checkbox' ? checked : value,
      ...(fieldName === 'has_access'
        ? {
            status: checked ? 'active' : 'inactive',
          }
        : null),
    }));
  }

  function handleSeaweedTabChange(tabId, checked) {
    setFormValues((current) => {
      const currentTabs = Array.isArray(current.seaweed_access_tabs)
        ? current.seaweed_access_tabs
        : DEFAULT_ACCOUNTANT_SEAWEED_ACCESS_TABS;
      const nextTabs = checked
        ? [...currentTabs, tabId]
        : currentTabs.filter((item) => item !== tabId);

      return {
        ...current,
        seaweed_access_tabs: SEAWEED_ADMIN_TAB_OPTIONS
          .map((item) => item.value)
          .filter((item) => nextTabs.includes(item)),
      };
    });
  }

  function handleToggleAllSeaweedTabs(checked) {
    setFormValues((current) => ({
      ...current,
      seaweed_access_tabs: checked
        ? SEAWEED_ADMIN_TAB_OPTIONS.map((item) => item.value)
        : [],
    }));
  }

  async function refreshUsers() {
    if (!selectedBusinessId) {
      return;
    }

    const response = await getUsers(token, {
      customer_id: selectedBusinessId,
    });
    setUsers(response.data || []);
  }

  async function handleToggleUserStatus(userId, currentStatus) {
    if (Number(userId) === Number(currentUser?.id)) {
      setError('You cannot disable access for the account you are currently using.');
      return;
    }

    setUpdatingUserId(userId);

    try {
      await updateUser(token, userId, {
        status: currentStatus === 'active' ? 'inactive' : 'active',
      });
      await refreshUsers();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setUpdatingUserId(null);
    }
  }

  function openResetPasswordModal(row) {
    setResetPasswordTarget(row);
    setResetPasswordError('');
    setResetPasswordResult(null);
    setHasCopiedTemporaryPassword(false);
  }

  function closeResetPasswordModal() {
    if (resetPasswordSubmitting) {
      return;
    }

    setResetPasswordTarget(null);
    setResetPasswordError('');
  }

  function closeResetPasswordResult() {
    setResetPasswordResult(null);
    setHasCopiedTemporaryPassword(false);
  }

  async function handleResetPassword() {
    if (!resetPasswordTarget) {
      return;
    }

    setResetPasswordSubmitting(true);
    setResetPasswordError('');

    try {
      const response = await resetUserPassword(token, resetPasswordTarget.id);
      setResetPasswordResult({
        username: response.data?.username || resetPasswordTarget.username,
        temporaryPassword: response.data?.temporary_password || '',
      });
      setResetPasswordTarget(null);
      await refreshUsers();
    } catch (requestError) {
      setResetPasswordError(requestError.message);
    } finally {
      setResetPasswordSubmitting(false);
    }
  }

  async function handleCopyTemporaryPassword() {
    const temporaryPassword = resetPasswordResult?.temporaryPassword;

    if (!temporaryPassword) {
      return;
    }

    try {
      await navigator.clipboard.writeText(temporaryPassword);
      setHasCopiedTemporaryPassword(true);
    } catch (error) {
      setHasCopiedTemporaryPassword(false);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!selectedBusinessId) {
      setSubmitError('Select a business first.');
      return;
    }

    if (!formValues.username.trim() || !formValues.email.trim()) {
      setSubmitError('Username and email are required.');
      return;
    }

    if (modalMode === 'create' && !formValues.password.trim()) {
      setSubmitError('Password is required.');
      return;
    }

    if (modalMode === 'create' && formValues.password.length < 8) {
      setSubmitError('Password must be at least 8 characters.');
      return;
    }

    const nextUserType = getUserTypeForRole(formValues.role_name);
    const nextBranchId =
      nextUserType === STAFF_USER_TYPE && formValues.branch_id ? Number(formValues.branch_id) : null;
    const isEditingCurrentUser =
      modalMode === 'edit' && Number(editingUserId) === Number(currentUser?.id);

    setSubmitting(true);
    setSubmitError('');

    try {
      const payload = {
        username: formValues.username.trim(),
        email: formValues.email.trim(),
        role_name: formValues.role_name.trim() || null,
        user_type: nextUserType,
        branch_id: nextBranchId,
        status: isEditingCurrentUser ? 'active' : formValues.has_access ? 'active' : 'inactive',
        seaweed_access_tabs:
          formValues.role_name === 'accountant'
            ? formValues.seaweed_access_tabs
            : null,
      };

      if (modalMode === 'edit') {
        await updateUser(token, editingUserId, payload);
      } else {
        await createUser(token, {
          ...payload,
          password: formValues.password,
          customer_id: selectedBusinessId,
        });
      }

      await refreshUsers();
      closeModal(true);
    } catch (requestError) {
      setSubmitError(requestError.message);
      setSubmitting(false);
    }
  }

  const branchNameMap = branches.reduce((accumulator, branch) => {
    accumulator[branch.id] = branch.name;
    return accumulator;
  }, {});

  const firstAdminUserId = users.reduce((firstAdminId, item) => {
    if (item.user_type !== ADMIN_USER_TYPE || item.role_name === 'admin-custom') {
      return firstAdminId;
    }

    const userId = Number(item.id);
    return firstAdminId === null || userId < firstAdminId ? userId : firstAdminId;
  }, null);
  const rows = users.map((item, index) => {
    const isFirstAdminAccount =
      item.user_type === ADMIN_USER_TYPE &&
      item.role_name !== 'admin-custom' &&
      Number(item.id) === firstAdminUserId;
    const isCurrentUser = Number(item.id) === Number(currentUser?.id);

    return {
      id: item.id,
      number: index + 1,
      username: item.username,
      email: item.email,
      user_type: item.user_type,
      role_name: getRoleOption(item.role_name, item.user_type)?.value || DEFAULT_ROLE_NAME,
      seaweed_access_tabs: item.seaweed_access_tabs,
      branch_id: item.branch_id,
      branch_name:
        item.user_type === ADMIN_USER_TYPE
          ? 'All branches'
          : item.branch_id
            ? branchNameMap[item.branch_id] || 'Assigned branch'
            : 'Unassigned',
      status: item.status,
      is_first_admin_account: isFirstAdminAccount,
      is_current_user: isCurrentUser,
      can_edit: item.user_type === STAFF_USER_TYPE || item.user_type === ADMIN_USER_TYPE,
      can_reset_password:
        currentUser?.user_type === ADMIN_USER_TYPE &&
        item.user_type !== undefined &&
        !isCurrentUser,
      can_toggle:
        !isFirstAdminAccount &&
        !isCurrentUser &&
        (item.user_type === STAFF_USER_TYPE || item.user_type === ADMIN_USER_TYPE),
    };
  });
  const filteredRows = rows.filter((row) => {
    const searchValue = filters.search.trim().toLowerCase();
    const matchesSearch = searchValue
      ? [row.username, row.email, row.role_name, row.branch_name]
          .join(' ')
          .toLowerCase()
          .includes(searchValue)
      : true;
    const matchesStatus = filters.status === 'all' || row.status === filters.status;

    return matchesSearch && matchesStatus;
  });
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / DEFAULT_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedRows = filteredRows.slice(
    (currentPage - 1) * DEFAULT_PAGE_SIZE,
    currentPage * DEFAULT_PAGE_SIZE
  );
  const activeUserCount = users.filter((item) => item.status === 'active').length;

  function updateFilter(name, value) {
    setFilters((current) => ({
      ...current,
      [name]: value,
    }));
  }

  function clearFilters() {
    setFilters({
      search: '',
      status: 'all',
    });
  }

  const columns = [
    { key: 'number', label: 'No.' },
    { key: 'username', label: 'Username' },
    { key: 'email', label: 'Email' },
    {
      key: 'role_name',
      label: 'Role',
      render: (row) => {
        const role = getRoleOption(row.role_name, row.user_type);

        return (
          <div className="grid max-w-[150px] gap-1 xl:max-w-none">
            <span className="font-medium capitalize text-[#111111]">{role.label}</span>
            <span className="truncate text-xs text-[#8b7a6d] xl:whitespace-normal">
              {role.description}
            </span>
          </div>
        );
      },
    },
    { key: 'branch_name', label: 'Branch' },
    {
      key: 'status',
      label: 'Status',
      render: (row) => (
        <span
          className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.08em] ${
            row.status === 'active'
              ? 'bg-[#edf7ef] text-[#4e7b56]'
              : 'bg-[#f5f2ee] text-[#93857b]'
          }`}
        >
          {row.status}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <div className="flex items-center gap-3">
          <TableActionButtons
            canEdit={row.can_edit}
            canResetPassword={row.can_reset_password}
            canToggle={row.can_toggle}
            onEdit={() => openEditModal(row)}
            onResetPassword={() => openResetPasswordModal(row)}
            onToggle={() => handleToggleUserStatus(row.id, row.status)}
            toggleStatus={row.status}
          />
          {row.can_toggle || row.is_current_user ? (
            <span className="text-xs text-[#8b7a6d]">
              {row.is_current_user
                ? 'Current account'
                : updatingUserId === row.id
                  ? 'Updating...'
                  : row.status === 'active'
                    ? 'Has access'
                    : 'No access'}
            </span>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <div className="flex flex-col items-start justify-between gap-3 lg:flex-row lg:items-center">
          <div>
            <h2 className="m-0 text-2xl font-semibold tracking-[-0.03em] text-[#111111] sm:text-3xl">
              User Management
            </h2>
            <p className="mt-1 text-[#8b7a6d]">
              Add admin, custom admin, and staff accounts under the selected business.
            </p>
          </div>
          <Button
            className="w-full justify-center px-4 py-3 sm:w-auto"
            onClick={openModal}
            disabled={!selectedBusiness}
          >
            Add User
          </Button>
        </div>

        {!selectedBusiness ? (
          <Card className="p-6 sm:p-8">
            <EmptyState
              icon={UserCog}
              title="Select a business first"
              message="Choose a business from the E-Portal view before managing account assignments."
              showAction={false}
            />
          </Card>
        ) : (
          <>
            <Card className="px-4 py-2.5 sm:px-5">
              <div className="flex flex-col gap-2.5 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#93857b]">
                    Managing users for
                  </p>
                  <h3 className="mt-1 truncate text-lg font-semibold tracking-[-0.02em] text-[#111111]">
                    {selectedBusiness.name}
                  </h3>
                </div>

                <div className="grid gap-2 sm:grid-cols-3 lg:min-w-[460px]">
                  <div className="flex items-center justify-between rounded-2xl border border-[#ece6df] bg-[#fcfbf9] px-3 py-1.5">
                    <span className="text-xs font-medium text-[#8b7a6d]">Branches</span>
                    <span className="text-base font-semibold text-[#111111]">{branches.length}</span>
                  </div>
                  <div className="flex items-center justify-between rounded-2xl border border-[#ece6df] bg-[#fcfbf9] px-3 py-1.5">
                    <span className="text-xs font-medium text-[#8b7a6d]">Users</span>
                    <span className="text-base font-semibold text-[#111111]">{users.length}</span>
                  </div>
                  <div className="flex items-center justify-between rounded-2xl border border-[#ece6df] bg-[#fcfbf9] px-3 py-1.5">
                    <span className="text-xs font-medium text-[#8b7a6d]">Active</span>
                    <span className="text-base font-semibold text-[#111111]">{activeUserCount}</span>
                  </div>
                </div>
              </div>
            </Card>

            {loading ? (
              <div className="rounded-2xl border border-[#e6ddd4] bg-white px-4 py-4 text-[#8b7a6d]">
                Loading users...
              </div>
            ) : null}

            {error ? (
              <div className="rounded-2xl border border-[#f0d4cb] bg-white px-4 py-4 text-[#b4533f]">
                {error}
              </div>
            ) : null}

            {!loading && !error ? (
              <Card className="flex min-h-0 flex-1 flex-col p-3 sm:p-4">
                <div className="flex min-h-[340px] flex-1 flex-col">
                  <div className="mb-3">
                    <SearchFilterBar
                      clearLabel="Clear"
                      onClear={clearFilters}
                      onSearchChange={(value) => updateFilter('search', value)}
                      onStatusChange={(value) => updateFilter('status', value)}
                      searchPlaceholder="Search users by name, email, role, or branch"
                      searchValue={filters.search}
                      statusValue={filters.status}
                    />
                  </div>

                  <Table
                    columns={columns}
                    rows={paginatedRows}
                    emptyState={{
                      icon: UserCog,
                      title: users.length ? 'No users match your filters' : 'No users yet',
                      message: users.length
                        ? 'Try clearing or changing the filters above.'
                        : 'Add the first account for this business and choose an account role.',
                      showAction: false,
                    }}
                  />

                  <PaginationControls
                    currentPage={currentPage}
                    itemLabel="users"
                    onPageChange={setPage}
                    pageSize={DEFAULT_PAGE_SIZE}
                    totalItems={filteredRows.length}
                    totalPages={totalPages}
                  />
                </div>
              </Card>
            ) : null}
          </>
        )}
      </div>

      <UserFormModal
        isOpen={isModalOpen}
        mode={modalMode}
        selectedBusinessName={selectedBusiness?.name}
        branches={branches}
        formValues={formValues}
        isRoleSelectionHidden={isEditingFirstAdminAccount}
        isSeaweedBusiness={selectedBusiness?.system_type === 'seaweed'}
        onChange={handleChange}
        onSeaweedTabChange={handleSeaweedTabChange}
        onToggleAllSeaweedTabs={handleToggleAllSeaweedTabs}
        onClose={closeModal}
        onSubmit={handleSubmit}
        submitError={submitError}
        submitting={submitting}
      />

      <ConfirmationModal
        isOpen={Boolean(resetPasswordTarget)}
        title="Reset Password"
        description={`Generate a 6-digit temporary password for ${resetPasswordTarget?.username || 'this admin account'}. They will be prompted to change it immediately after login.`}
        confirmLabel="Reset Password"
        cancelLabel="Cancel"
        onConfirm={handleResetPassword}
        onCancel={closeResetPasswordModal}
        isLoading={resetPasswordSubmitting}
      />

      {resetPasswordError && resetPasswordTarget ? (
        <div className="fixed bottom-5 right-5 z-[60] max-w-[360px] rounded-2xl border border-[#f0d4cb] bg-white px-4 py-3 text-sm text-[#b4533f] shadow-[0_18px_40px_rgba(17,17,17,0.14)]">
          {resetPasswordError}
        </div>
      ) : null}

      <ResetPasswordResultModal
        isOpen={Boolean(resetPasswordResult)}
        username={resetPasswordResult?.username}
        temporaryPassword={resetPasswordResult?.temporaryPassword}
        hasCopied={hasCopiedTemporaryPassword}
        onCopy={handleCopyTemporaryPassword}
        onClose={closeResetPasswordResult}
      />
    </>
  );
}

function ResetPasswordResultModal({
  isOpen,
  username,
  temporaryPassword,
  hasCopied,
  onCopy,
  onClose,
}) {
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const { body } = document;
    const previousOverflow = body.style.overflow;
    body.style.overflow = 'hidden';

    return () => {
      body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-[rgba(17,17,17,0.26)] px-4 py-6 backdrop-blur-[4px]">
      <div className="w-full max-w-[430px] overflow-hidden rounded-[30px] border border-[#e9e0d8] bg-[linear-gradient(180deg,#fdfcfb_0%,#faf6f1_100%)] shadow-[0_24px_60px_rgba(17,17,17,0.14)]">
        <div className="border-b border-[#ebe3db] px-5 py-5 sm:px-6">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[20px] border border-[#e7ddd3] bg-[#f7f4ef] text-[#6f5644]">
              <KeyRound className="h-6 w-6" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="m-0 text-[1.22rem] font-semibold tracking-[-0.02em] text-[#111111]">
                Temporary Password Ready
              </h3>
              <p className="mt-2 text-sm leading-6 text-[#7f736b]">
                Send this code to {username || 'the user'}. They must change their password after login.
              </p>
            </div>
          </div>
        </div>

        <div className="grid gap-4 px-5 py-5 sm:px-6">
          <div className="flex items-center justify-between gap-3 rounded-[20px] border border-[#e6ddd4] bg-white px-4 py-3 shadow-[0_8px_20px_rgba(17,17,17,0.04)]">
            <span className="font-mono text-2xl font-bold tracking-[0.18em] text-[#111111]">
              {temporaryPassword}
            </span>
            <button
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] border border-[#d8d1ca] bg-[#f7f4ef] text-[#6f5644] transition hover:bg-[#efe8e1]"
              type="button"
              onClick={onCopy}
              title={hasCopied ? 'Copied' : 'Copy temporary password'}
              aria-label={hasCopied ? 'Copied' : 'Copy temporary password'}
            >
              {hasCopied ? <Check className="h-4.5 w-4.5" /> : <Copy className="h-4.5 w-4.5" />}
            </button>
          </div>

          <Button
            className="w-full justify-center rounded-[16px] px-4.5 py-2.5 text-[14px] font-semibold"
            type="button"
            onClick={onClose}
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}

export default UserManagementView;
